import { useEffect, useMemo, useState } from "react"
import { Accordion, Alert, Badge, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap"
import { FaCheckCircle, FaCircleNotch, FaCog, FaExclamationTriangle, FaGlobe, FaRegCircle, FaSearch } from "react-icons/fa"
import useFetch from "../../../_fetch"
import { getColor } from "../../../_utils"
import { useAuth } from "../../../auth/auth-context"
import { NetworkForm } from "./network-form"
import { NetworkTable } from "./network-table"
import "./network-settings.css"

export type INetworkIntegration = {
    name: string
    fields: ({ name: string; secret?: boolean, displayName?: string } | { name: string; multiple: boolean; attributes: string[]; secret?: boolean, displayName?: string, displayAttributes?: string[] })[]
    values: ({ [key: string]: string | boolean | { [key: string]: string }[] | undefined } & { _id?: string; _wasInitialized?: boolean; _isDisabled?: boolean; _hasError?: boolean })[]
    description?: string
}

export type INetworkSettings = {
    networkIntegrations: INetworkIntegration[],
    userSettings: { webhookURL?: string }
}

function isConnectedCredential(network: INetworkIntegration, credential: INetworkIntegration['values'][number]) {
    return credential._wasInitialized === true && !credential._isDisabled &&
        (network.name !== 'Skimlinks' || Boolean(credential.site_id))
}

export default function Settings() {
    const { doFetch } = useFetch()
    const { user } = useAuth()
    const isPublisher = user?.kind === "publisher"
    const [networkData, setNetworkData] = useState<INetworkIntegration[]>([])
    const [activeNetwork, setActiveNetwork] = useState<INetworkIntegration>()
    const [callbackUrl, setCallbackUrl] = useState("")
    const [isUrlValid, setIsUrlValid] = useState(true)
    const [isSaving, setIsSaving] = useState(false)
    const [searchTerm, setSearchTerm] = useState("")
    const [isLoading, setIsLoading] = useState(true)
    const [loadError, setLoadError] = useState("")
    const [saveError, setSaveError] = useState("")
    const [saveSuccess, setSaveSuccess] = useState(false)

    const fetchUserData = async () => {
        try {
            setLoadError("")
            const res = await doFetch("/network-settings")
            if (!res.ok) throw new Error("Network settings could not be loaded.")
            const nwSettings = (await res.json()) as INetworkSettings
            const networks = [...(nwSettings.networkIntegrations || [])].sort((a, b) => a.name.localeCompare(b.name))
            setCallbackUrl(nwSettings.userSettings?.webhookURL || "")
            setNetworkData(networks)
            setActiveNetwork((current) => networks.find((network) => network.name === current?.name) || networks[0])
        } catch (error) {
            console.error("Error fetching user data:", error)
            setLoadError("Network settings could not be loaded. Please try again.")
        } finally {
            setIsLoading(false)
        }
    }

    useEffect(() => {
        void fetchUserData()
        // The fetch helper is stable for the lifetime of this page.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [])

    const isValidUrl = (url: string): boolean => {
        if (url === "") return true
        try {
            return ["http:", "https:"].includes(new URL(url).protocol)
        } catch {
            return false
        }
    }

    const handleUrlChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const url = event.target.value
        setCallbackUrl(url)
        setIsUrlValid(isValidUrl(url))
        setSaveError("")
        setSaveSuccess(false)
    }

    const saveSettings = async () => {
        if (!isUrlValid) return
        setIsSaving(true)
        setSaveError("")
        setSaveSuccess(false)
        try {
            const res = await doFetch("/network/update-user-settings", { settings: { webhookURL: callbackUrl } })
            if (res.status >= 400) throw new Error("Error saving settings.")
            setSaveSuccess(true)
        } catch (error) {
            console.error("Error saving settings:", error)
            setSaveError("Webhook settings could not be saved. Please try again.")
        } finally {
            setIsSaving(false)
        }
    }

    const filteredNetworks = useMemo(() => {
        const query = searchTerm.trim().toLowerCase()
        return query ? networkData.filter((network) => network.name.toLowerCase().includes(query)) : networkData
    }, [networkData, searchTerm])

    const configuredNetworks = networkData.filter((network) => network.values.some((credential) =>
        credential._wasInitialized === true && (network.name !== 'Skimlinks' || Boolean(credential.site_id)))).length
    const allNetworksEmpty = networkData.length > 0 && configuredNetworks === 0
    const activeCredentialCount = activeNetwork?.values.filter((credential) => isConnectedCredential(activeNetwork, credential)).length || 0
    const activeDisabledCount = activeNetwork?.values.filter((credential) => credential._isDisabled === true).length || 0
    const activeHasSetupError = activeNetwork?.values.some((credential) => credential._hasError === true && !credential._isDisabled) || false
    const activeNeedsSiteId = activeNetwork?.name === 'Skimlinks' && activeNetwork.values.some((credential) => credential._wasInitialized === true && !credential._isDisabled && !credential.site_id)
    const activeIsSynchronizing = !activeHasSetupError && !activeNeedsSiteId && !activeCredentialCount && !activeDisabledCount && Boolean(activeNetwork?.values.length)
    const activeNetworkIndex = activeNetwork ? networkData.findIndex((network) => network.name === activeNetwork.name) : -1
    const activeNetworkColor = activeNetwork ? getColor(activeNetwork.name, Math.max(activeNetworkIndex, 0)) || "#64748b" : "#64748b"
    const placeholders = ["id", "commission", "date", "country", "network", "clickReference", "campaignId", "campaignName"]
    if (!isPublisher) placeholders.push("affiliateId", "affiliateName", "orderAmount", "orderReference")

    return (
        <div className="container-fluid px-2 px-md-3 network-settings-page">
            <Card className="network-settings-hero border-0 mb-4">
                <Card.Body className="d-flex flex-column flex-lg-row justify-content-between gap-3 align-items-lg-center">
                    <div>
                        <div className="network-settings-eyebrow">Integrations</div>
                        <h4 className="mb-1">Network settings</h4>
                        <div className="text-muted">Connect affiliate networks and manage how conversion data is delivered.</div>
                    </div>
                    <div className="d-flex flex-wrap gap-2">
                        <Badge bg="light" text="dark" className="network-settings-summary-badge">
                            <FaGlobe aria-hidden="true" /> {networkData.length} networks
                        </Badge>
                        <Badge bg={configuredNetworks ? "success" : "light"} text={configuredNetworks ? undefined : "dark"} className="network-settings-summary-badge">
                            <FaCheckCircle aria-hidden="true" /> {configuredNetworks} configured
                        </Badge>
                    </div>
                </Card.Body>
            </Card>

            {loadError && <Alert variant="danger" className="mb-4">{loadError}</Alert>}

            {isLoading ? (
                <Card className="network-settings-panel border-0"><Card.Body className="text-center py-5"><Spinner animation="border" /><div className="text-muted small mt-2">Loading networks…</div></Card.Body></Card>
            ) : (
                <>
                    {networkData.length > 0 && (
                        <Alert variant={allNetworksEmpty ? "warning" : "info"} className="network-settings-notice mb-4">
                            <div className="fw-semibold mb-1">{allNetworksEmpty ? "Connect a network to activate reporting" : "New credentials are being synchronized automatically"}</div>
                            <div className="small">
                                Data can take up to 30 minutes to appear while up to two years of history is retrieved. Need help? Read the <a href="https://nordpar.com/affiliate-marketing-api/" target="_blank" rel="noopener noreferrer">network setup guide</a>.
                            </div>
                        </Alert>
                    )}

                    <Row className="g-4 align-items-start network-settings-main-grid">
                        <Col lg={4} xl={3}>
                            <Card className="network-settings-panel network-settings-sidebar border-0">
                                <Card.Header className="bg-white border-0 network-settings-card-header">
                                    <div className="network-settings-eyebrow">Network overview</div>
                                    <h5 className="mb-0">Networks</h5>
                                </Card.Header>
                                <Card.Body className="pt-2">
                                    <div className="network-settings-card-intro">Select a network to manage credentials.</div>
                                    <div className="network-settings-search mb-3">
                                        <FaSearch aria-hidden="true" />
                                        <Form.Control
                                            type="search"
                                            aria-label="Search networks"
                                            placeholder="Search networks"
                                            value={searchTerm}
                                            onChange={(event) => setSearchTerm(event.target.value)}
                                        />
                                    </div>
                                    <div className="network-settings-network-list">
                                        {filteredNetworks.map((network) => {
                                            const isActive = network.name === activeNetwork?.name
                                            const networkIndex = networkData.findIndex((item) => item.name === network.name)
                                            const networkColor = getColor(network.name, Math.max(networkIndex, 0)) || "#64748b"
                                            const credentialCount = network.values.filter((credential) => isConnectedCredential(network, credential)).length
                                            const disabledCount = network.values.filter((credential) => credential._isDisabled === true).length
                                            const hasSetupError = network.values.some((credential) => credential._hasError === true && !credential._isDisabled)
                                            const needsSiteId = network.name === 'Skimlinks' && network.values.some((credential) => credential._wasInitialized === true && !credential._isDisabled && !credential.site_id)
                                            const isSynchronizing = !hasSetupError && !needsSiteId && !credentialCount && !disabledCount && network.values.length > 0
                                            return (
                                                <button
                                                    type="button"
                                                    key={network.name}
                                                    className={`network-settings-network-item ${isActive ? "is-active" : ""} ${isSynchronizing ? "is-synchronizing" : ""}`}
                                                    onClick={() => setActiveNetwork(network)}
                                                    aria-current={isActive ? "true" : undefined}
                                                >
                                                    <span
                                                        className="network-settings-network-color"
                                                        style={{ backgroundColor: networkColor }}
                                                        aria-hidden="true"
                                                    />
                                                    <span className="network-settings-network-copy">
                                                        <span className="network-settings-network-name">{network.name}</span>
                                                        <span className={`network-settings-network-status ${isSynchronizing ? "is-synchronizing" : ""}`}>
                                                            {needsSiteId ? "Site ID required" : hasSetupError ? "Setup issue" : credentialCount ? `${credentialCount} credential${credentialCount === 1 ? "" : "s"}` : disabledCount ? "Disabled" : isSynchronizing ? "Synchronizing" : "Not configured"}
                                                        </span>
                                                    </span>
                                                    {needsSiteId || hasSetupError ? (
                                                        <FaExclamationTriangle className="text-warning" aria-label="Setup issue" />
                                                    ) : credentialCount ? (
                                                        <FaCheckCircle className="text-success" aria-label="Configured" />
                                                    ) : isSynchronizing ? (
                                                        <FaCircleNotch className="network-settings-list-sync-icon" aria-label="Synchronizing" />
                                                    ) : (
                                                        <FaRegCircle className="network-settings-not-configured-icon" aria-label="Not configured" />
                                                    )}
                                                </button>
                                            )
                                        })}
                                        {!filteredNetworks.length && <div className="network-settings-empty py-4">No networks match “{searchTerm}”.</div>}
                                    </div>
                                </Card.Body>
                            </Card>
                        </Col>

                        <Col lg={8} xl={9}>
                            {activeNetwork ? (
                                <Card className="network-settings-panel border-0">
                                    <Card.Header className="bg-white border-0 d-flex flex-column flex-sm-row justify-content-between gap-2 align-items-sm-center network-settings-card-header">
                                        <div>
                                            <div className="network-settings-eyebrow">Selected network</div>
                                            <h5 className="network-settings-selected-name mb-0">
                                                <span className="network-settings-selected-color" style={{ backgroundColor: activeNetworkColor }} aria-hidden="true" />
                                                {activeNetwork.name}
                                            </h5>
                                        </div>
                                        <div className={`network-settings-state ${activeNeedsSiteId || activeHasSetupError ? "has-error" : activeCredentialCount ? "is-configured" : activeIsSynchronizing ? "is-synchronizing" : ""}`}>
                                            {activeNeedsSiteId || activeHasSetupError ? (
                                                <FaExclamationTriangle aria-hidden="true" />
                                            ) : activeCredentialCount ? (
                                                <FaCheckCircle aria-hidden="true" />
                                            ) : activeDisabledCount ? (
                                                <FaRegCircle aria-hidden="true" />
                                            ) : activeIsSynchronizing ? (
                                                <FaCircleNotch aria-hidden="true" />
                                            ) : (
                                                <span className="network-settings-state-dot" aria-hidden="true" />
                                            )}
                                            <span>
                                                {activeNeedsSiteId
                                                    ? "Site ID required"
                                                    : activeHasSetupError
                                                    ? "Setup issue"
                                                    : activeCredentialCount
                                                        ? `${activeCredentialCount} credential${activeCredentialCount === 1 ? "" : "s"} connected`
                                                        : activeDisabledCount
                                                            ? "Disabled"
                                                        : activeIsSynchronizing
                                                            ? "Synchronizing"
                                                            : "Setup required"}
                                            </span>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <Row className="g-4">
                                            <Col xl={5}>
                                                <div className="network-settings-workspace-section">
                                                    <NetworkForm key={`form-${activeNetwork.name}`} network={activeNetwork} fetchUserData={fetchUserData} />
                                                </div>
                                            </Col>
                                            <Col xl={7}>
                                                <div className="network-settings-workspace-section">
                                                    <NetworkTable key={`table-${activeNetwork.name}`} network={activeNetwork} fetchUserData={fetchUserData} />
                                                </div>
                                            </Col>
                                        </Row>
                                        {activeNetwork.description && (
                                            <div className="network-settings-description mt-4" dangerouslySetInnerHTML={{ __html: activeNetwork.description }} />
                                        )}
                                    </Card.Body>
                                </Card>
                            ) : (
                                <Card className="network-settings-panel border-0"><Card.Body className="network-settings-empty py-5">Select a network to manage its credentials.</Card.Body></Card>
                            )}
                        </Col>
                    </Row>

                    <Card className="network-settings-panel border-0 mt-4 mb-5">
                        <Card.Header className="bg-white border-0 d-flex align-items-center gap-3">
                            <span className="network-settings-icon-box"><FaCog aria-hidden="true" /></span>
                            <div>
                                <div className="fw-semibold">Global webhook settings</div>
                                <div className="text-muted small">Send registered sales to your own endpoint.</div>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            {saveSuccess && <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)}>Webhook settings saved.</Alert>}
                            {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError("")}>{saveError}</Alert>}
                            <Form.Group controlId="callbackUrl">
                                <Form.Label className="fw-semibold">Custom webhook URL</Form.Label>
                                <Form.Control
                                    type="url"
                                    placeholder="https://your-domain.com/webhook"
                                    value={callbackUrl}
                                    onChange={handleUrlChange}
                                    isInvalid={!isUrlValid && callbackUrl !== ""}
                                />
                                <Form.Control.Feedback type="invalid">Enter a valid URL beginning with http:// or https://.</Form.Control.Feedback>
                                <Form.Text className="text-muted">This URL is called whenever a sale is registered.</Form.Text>
                            </Form.Group>

                            <Accordion className="network-settings-webhook-help mt-3">
                                <Accordion.Item eventKey="0">
                                    <Accordion.Header>Webhook placeholders and example</Accordion.Header>
                                    <Accordion.Body>
                                        <div className="text-muted small mb-2">Placeholders are replaced with the actual conversion values.</div>
                                        <div className="d-flex flex-wrap gap-2 mb-3">
                                            {placeholders.map((placeholder) => <code key={placeholder} className="network-settings-token">{`{${placeholder}}`}</code>)}
                                        </div>
                                        <div className="small mb-2">Currency values use <strong>{user?.settings?.currency || "your selected currency"}</strong>.</div>
                                        <div className="network-settings-code-example"><code>https://mydomain.org/?id={"{id}"}&commission={"{commission}"}&network={"{network}"}</code></div>
                                    </Accordion.Body>
                                </Accordion.Item>
                            </Accordion>

                            <div className="d-flex justify-content-end mt-3">
                                <Button variant="primary" onClick={saveSettings} disabled={!isUrlValid || isSaving}>
                                    {isSaving ? <><Spinner as="span" animation="border" size="sm" aria-hidden="true" /><span className="ms-2">Saving…</span></> : "Save webhook"}
                                </Button>
                            </div>
                        </Card.Body>
                    </Card>
                </>
            )}
        </div>
    )
}
